import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const arabicLetters = [
  { letter: "ا", pronunciation: "Alif" },
  { letter: "ب", pronunciation: "Ba" },
  { letter: "ت", pronunciation: "Ta" },
  { letter: "ث", pronunciation: "Tha" },
  { letter: "ج", pronunciation: "Jeem" },
  { letter: "ح", pronunciation: "Ha" },
  { letter: "خ", pronunciation: "Kha" },
  { letter: "د", pronunciation: "Dal" },
  { letter: "ذ", pronunciation: "Dhal" },
  { letter: "ر", pronunciation: "Ra" },
  { letter: "ز", pronunciation: "Zay" },
  { letter: "س", pronunciation: "Seen" },
  { letter: "ش", pronunciation: "Sheen" },
  { letter: "ص", pronunciation: "Sad" },
  { letter: "ض", pronunciation: "Dad" },
  { letter: "ط", pronunciation: "Taa" },
  { letter: "ظ", pronunciation: "Zaa" },
  { letter: "ع", pronunciation: "Ain" },
  { letter: "غ", pronunciation: "Ghain" },
  { letter: "ف", pronunciation: "Fa" },
  { letter: "ق", pronunciation: "Qaf" },
  { letter: "ك", pronunciation: "Kaf" },
  { letter: "ل", pronunciation: "Lam" },
  { letter: "م", pronunciation: "Meem" },
  { letter: "ن", pronunciation: "Noon" },
  { letter: "هـ", pronunciation: "Ha" },
  { letter: "و", pronunciation: "Waw" },
  { letter: "ي", pronunciation: "Ya" },
];

function AlphabetLesson() {
  const [letters, setLetters] = useState(arabicLetters);
  const [selectedPronunciation, setSelectedPronunciation] = useState("");
  const [clickedLetters, setClickedLetters] = useState(new Set());

  const handleClick = (letter, pronunciation) => {
    setSelectedPronunciation(pronunciation);
    setClickedLetters((prev) => new Set(prev).add(letter));
    window.responsiveVoice.speak(letter, "Arabic Male");
  };

  const shuffleLetters = () => {
    const shuffled = [...letters].sort(() => Math.random() - 0.5);
    setLetters(shuffled);
    setSelectedPronunciation("");
  };

  const resetLetters = () => {
    setLetters(arabicLetters);
    setSelectedPronunciation("");
    setClickedLetters(new Set());
  };

  const progress = (clickedLetters.size / arabicLetters.length) * 100;

  return (
    <div dir="rtl" className="p-4 sm:p-6 text-center font-arabic min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-blue-800">الحروف العربية</h2>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-green-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">
        تم تعلم {clickedLetters.size} من 28 حرف
      </p>
      {/* Pronunciation Display */}
      <AnimatePresence>
        {selectedPronunciation && (
          <motion.div
            key="pronunciation"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-xl sm:text-2xl font-semibold text-green-700 mb-6"
          >
            النطق: {selectedPronunciation}
          </motion.div>
        )}
      </AnimatePresence>
      {/* Letters Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-4 justify-center mb-6">
        {letters.map(({ letter, pronunciation }, index) => {
          const learned = clickedLetters.has(letter);
          return (
            <button
              key={index}
              onClick={() => handleClick(letter, pronunciation)}
              className={`text-3xl sm:text-4xl font-bold p-5 rounded-2xl shadow-lg transition duration-200 ${
                learned
                  ? "bg-green-100 text-green-700"
                  : "bg-white text-blue-700 hover:bg-blue-200"
              }`}
              title={pronunciation}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
        <button
          onClick={resetLetters}
          className="bg-green-500 hover:bg-green-600 text-white text-lg px-6 py-3 rounded-full shadow-lg transition duration-200 w-64 sm:w-auto"
        >
          إعادة الترتيب - Reset
        </button>
        <button
          onClick={shuffleLetters}
          className="bg-yellow-500 hover:bg-yellow-600 text-white text-lg px-6 py-3 rounded-full shadow-lg transition duration-200 w-64 sm:w-auto"
        >
          خلط الحروف - Shuffle
        </button>
      </div>

      {/* ResponsiveVoice Script */}
      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default AlphabetLesson;
